import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiGet, apiPost, apiPut, apiDelete } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import { Building2, Plus, Edit2, Users, X, Eye, EyeOff, LogIn, CreditCard, Calendar, AlertTriangle, CheckCircle, Clock, Shield, Trash2, Puzzle } from 'lucide-react'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import RolePermissionsModal from '../../components/admin/RolePermissionsModal'

interface Company {
  id: number
  name: string
  code: string
  email?: string
  phone?: string
  address?: string
  is_active: boolean
  user_count?: number
  subscription_plan?: string
  subscription_start?: string
  subscription_end?: string
  subscription_status?: string
  days_until_expiry?: number | null
  max_users?: number
  subscription_notes?: string
  enabled_modules?: Record<string, boolean>
}

interface CompanyFormData {
  name: string
  code: string
  email: string
  phone: string
  address: string
  admin: {
    name: string
    email: string
    password: string
    phone: string
  }
}

interface SubscriptionFormData {
  subscription_plan: string
  subscription_start: string
  subscription_end: string
  max_users: number
  subscription_notes: string
}

const initialFormData: CompanyFormData = {
  name: '',
  code: '',
  email: '',
  phone: '',
  address: '',
  admin: {
    name: '',
    email: '',
    password: '',
    phone: '',
  }
}

const initialSubscriptionData: SubscriptionFormData = {
  subscription_plan: 'trial',
  subscription_start: '',
  subscription_end: '',
  max_users: 10,
  subscription_notes: ''
}

const SUBSCRIPTION_PLANS = [
  { value: 'trial', label: 'Trial', color: 'gray', maxUsers: 5 },
  { value: 'basic', label: 'Basic', color: 'blue', maxUsers: 20 },
  { value: 'professional', label: 'Professional', color: 'purple', maxUsers: 50 },
  { value: 'enterprise', label: 'Enterprise', color: 'amber', maxUsers: 999 },
]

export default function CompaniesPage() {
  const navigate = useNavigate()
  const { impersonate } = useAuthStore()
  const [companies, setCompanies] = useState<Company[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editingCompany, setEditingCompany] = useState<Company | null>(null)
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null)
  const [formData, setFormData] = useState<CompanyFormData>(initialFormData)
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionFormData>(initialSubscriptionData)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [impersonatingId, setImpersonatingId] = useState<number | null>(null)
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false)
  const [permissionsCompany, setPermissionsCompany] = useState<Company | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<Company | null>(null)
  const [deleteTyped, setDeleteTyped] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [isModulesModalOpen, setIsModulesModalOpen] = useState(false)
  const [modulesCompany, setModulesCompany] = useState<Company | null>(null)
  const [modulesData, setModulesData] = useState<Record<string, boolean>>({ kpi: true, attendance: true, leave: true, timelog: true, project_monitoring: true })
  const [isModulesSaving, setIsModulesSaving] = useState(false)

  useEffect(() => {
    fetchCompanies()
  }, [])

  const fetchCompanies = async () => {
    try {
      const response = await apiGet<{ items: Company[] }>('/companies')
      setCompanies(response.items)
    } catch (error) {
      console.error('Failed to load companies:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)

    try {
      if (editingCompany) {
        await apiPut(`/companies/${editingCompany.id}`, {
          name: formData.name,
          code: formData.code,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
        })
      } else {
        await apiPost('/companies', {
          name: formData.name,
          code: formData.code,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          admin: {
            name: formData.admin.name,
            email: formData.admin.email,
            password: formData.admin.password,
            phone: formData.admin.phone,
          }
        })
      }

      setIsModalOpen(false)
      setEditingCompany(null)
      setFormData(initialFormData)
      fetchCompanies()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save company')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubscriptionSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedCompany) return

    setError('')
    setIsSubmitting(true)

    try {
      await apiPut(`/companies/${selectedCompany.id}/subscription`, {
        subscription_plan: subscriptionData.subscription_plan,
        subscription_start: subscriptionData.subscription_start || null,
        subscription_end: subscriptionData.subscription_end || null,
        max_users: subscriptionData.max_users,
        subscription_notes: subscriptionData.subscription_notes
      })

      setIsSubscriptionModalOpen(false)
      setSelectedCompany(null)
      setSubscriptionData(initialSubscriptionData)
      fetchCompanies()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update subscription')
    } finally {
      setIsSubmitting(false)
    }
  }

  const openCreateModal = () => {
    setEditingCompany(null)
    setFormData(initialFormData)
    setError('')
    setIsModalOpen(true)
  }

  const openEditModal = (company: Company) => {
    setEditingCompany(company)
    setFormData({
      name: company.name,
      code: company.code,
      email: company.email || '',
      phone: company.phone || '',
      address: company.address || '',
      admin: { name: '', email: '', password: '', phone: '' }
    })
    setError('')
    setIsModalOpen(true)
  }

  const openSubscriptionModal = (company: Company) => {
    setSelectedCompany(company)
    setSubscriptionData({
      subscription_plan: company.subscription_plan || 'trial',
      subscription_start: company.subscription_start || '',
      subscription_end: company.subscription_end || '',
      max_users: company.max_users || 10,
      subscription_notes: company.subscription_notes || ''
    })
    setError('')
    setIsSubscriptionModalOpen(true)
  }

  const generateCode = () => {
    const code = formData.name
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .substring(0, 6)
    setFormData({ ...formData, code })
  }

  const handleImpersonate = async (company: Company) => {
    setImpersonatingId(company.id)
    try {
      await impersonate(company.id)
      navigate('/admin')
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to impersonate')
    } finally {
      setImpersonatingId(null)
    }
  }

  const handleDeleteCompany = async () => {
    if (!deleteConfirm) return
    setIsDeleting(true)
    try {
      await apiDelete(`/companies/${deleteConfirm.id}`)
      setDeleteConfirm(null)
      setDeleteTyped('')
      fetchCompanies()
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete company')
    } finally {
      setIsDeleting(false)
    }
  }

  const openModulesModal = (company: Company) => {
    setModulesCompany(company)
    // Merge defaults so newly-added modules show as ON until explicitly toggled off.
    // (Mirrors backend has_module() which defaults missing keys to True.)
    setModulesData({
      kpi: true, attendance: true, leave: true, timelog: true, project_monitoring: true, learning: true,
      ...(company.enabled_modules || {}),
    })
    setIsModulesModalOpen(true)
  }

  const handleModulesSave = async () => {
    if (!modulesCompany) return
    setIsModulesSaving(true)
    try {
      await apiPut(`/companies/${modulesCompany.id}/modules`, { enabled_modules: modulesData })
      setIsModulesModalOpen(false)
      setModulesCompany(null)
      fetchCompanies()
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to update modules')
    } finally {
      setIsModulesSaving(false)
    }
  }

  const getActiveModuleCount = (company: Company) => {
    const modules = {
      kpi: true, attendance: true, leave: true, timelog: true, project_monitoring: true, learning: true,
      ...(company.enabled_modules || {}),
    }
    const active = Object.values(modules).filter(Boolean).length
    const total = Object.keys(modules).length
    return { active, total }
  }

  const getSubscriptionStatusBadge = (status?: string, daysLeft?: number | null) => {
    if (status === 'expired') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <AlertTriangle className="h-3 w-3" />
          Expired
        </span>
      )
    }
    if (status === 'expiring_soon') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
          <Clock className="h-3 w-3" />
          {daysLeft} days left
        </span>
      )
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle className="h-3 w-3" />
        Active
      </span>
    )
  }

  const getPlanBadge = (plan?: string) => {
    const planInfo = SUBSCRIPTION_PLANS.find(p => p.value === plan) || SUBSCRIPTION_PLANS[0]
    const colorClasses: Record<string, string> = {
      gray: 'bg-gray-100 text-gray-700',
      blue: 'bg-blue-100 text-blue-700',
      purple: 'bg-purple-100 text-purple-700',
      amber: 'bg-amber-100 text-amber-700'
    }
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colorClasses[planInfo.color]}`}>
        {planInfo.label}
      </span>
    )
  }

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
          <p className="text-gray-500 mt-1">Manage tenant companies and subscriptions</p>
        </div>
        <button onClick={openCreateModal} className="btn-primary">
          <Plus className="h-5 w-5 mr-2" /> Add Company
        </button>
      </div>

      {/* Companies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {companies.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            No companies yet. Click "Add Company" to create one.
          </div>
        ) : (
          companies.map((company) => (
            <div key={company.id} className="card hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-primary-100 rounded-lg">
                    <Building2 className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{company.name}</h3>
                    <p className="text-sm text-gray-500">Code: {company.code}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openSubscriptionModal(company)}
                    className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                    title="Manage subscription"
                  >
                    <CreditCard className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => openModulesModal(company)}
                    className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded"
                    title="Module toggles"
                  >
                    <Puzzle className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      setPermissionsCompany(company)
                      setIsPermissionsModalOpen(true)
                    }}
                    className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                    title="Role permissions"
                  >
                    <Shield className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleImpersonate(company)}
                    disabled={!company.is_active || impersonatingId === company.id}
                    className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Login as company admin"
                  >
                    {impersonatingId === company.id ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <LogIn className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => openEditModal(company)}
                    className="p-2 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded"
                    title="Edit company"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => { setDeleteConfirm(company); setDeleteTyped('') }}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Delete company"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Subscription Info */}
              <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getPlanBadge(company.subscription_plan)}
                    {getSubscriptionStatusBadge(company.subscription_status, company.days_until_expiry)}
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Users className="h-4 w-4" />
                    <span>{company.user_count || 0} / {company.max_users || 10} users</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-700">
                      <Puzzle className="h-3 w-3" />
                      {getActiveModuleCount(company).active}/{getActiveModuleCount(company).total}
                    </span>
                    <span className={company.is_active ? 'badge-success' : 'badge-danger'}>
                      {company.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                {company.subscription_end && (
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Calendar className="h-3 w-3" />
                    <span>Expires: {new Date(company.subscription_end).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Company Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-bold">
                {editingCompany ? 'Edit Company' : 'Create New Company'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-6">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Company Details */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Company Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      className="input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="PT. Example Company"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Code *
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        className="input flex-1"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        placeholder="EXMPL"
                        maxLength={10}
                        required
                      />
                      <button
                        type="button"
                        onClick={generateCode}
                        className="btn-secondary text-sm px-3"
                      >
                        Auto
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Email
                    </label>
                    <input
                      type="email"
                      className="input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="company@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Phone
                    </label>
                    <input
                      type="text"
                      className="input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+62 21 1234567"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <textarea
                      className="input"
                      rows={2}
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Company address..."
                    />
                  </div>
                </div>
              </div>

              {/* Admin Account - Only for new company */}
              {!editingCompany && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Admin Account</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Create an admin account for this company. They will manage users and KPI data.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Admin Name *
                      </label>
                      <input
                        type="text"
                        className="input"
                        value={formData.admin.name}
                        onChange={(e) => setFormData({
                          ...formData,
                          admin: { ...formData.admin, name: e.target.value }
                        })}
                        placeholder="John Doe"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Admin Email *
                      </label>
                      <input
                        type="email"
                        className="input"
                        value={formData.admin.email}
                        onChange={(e) => setFormData({
                          ...formData,
                          admin: { ...formData.admin, email: e.target.value }
                        })}
                        placeholder="admin@company.com"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Admin Password *
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          className="input pr-10"
                          value={formData.admin.password}
                          onChange={(e) => setFormData({
                            ...formData,
                            admin: { ...formData.admin, password: e.target.value }
                          })}
                          placeholder="Min. 8 characters"
                          minLength={8}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Admin Phone
                      </label>
                      <input
                        type="text"
                        className="input"
                        value={formData.admin.phone}
                        onChange={(e) => setFormData({
                          ...formData,
                          admin: { ...formData.admin, phone: e.target.value }
                        })}
                        placeholder="+62 812 3456 7890"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="btn-secondary"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Saving...</span>
                    </>
                  ) : (
                    editingCompany ? 'Update Company' : 'Create Company'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {isSubscriptionModalOpen && selectedCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
              <div>
                <h2 className="text-xl font-bold">Manage Subscription</h2>
                <p className="text-sm text-gray-500">{selectedCompany.name}</p>
              </div>
              <button
                onClick={() => setIsSubscriptionModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubscriptionSubmit} className="p-4 space-y-4 overflow-y-auto flex-1">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Plan Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subscription Plan
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {SUBSCRIPTION_PLANS.map((plan) => (
                    <button
                      key={plan.value}
                      type="button"
                      onClick={() => setSubscriptionData({
                        ...subscriptionData,
                        subscription_plan: plan.value,
                        max_users: plan.maxUsers
                      })}
                      className={`p-3 rounded-lg border-2 text-left transition-colors ${
                        subscriptionData.subscription_plan === plan.value
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium">{plan.label}</div>
                      <div className="text-xs text-gray-500">Up to {plan.maxUsers} users</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    className="input"
                    value={subscriptionData.subscription_start}
                    onChange={(e) => setSubscriptionData({
                      ...subscriptionData,
                      subscription_start: e.target.value
                    })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    className="input"
                    value={subscriptionData.subscription_end}
                    onChange={(e) => setSubscriptionData({
                      ...subscriptionData,
                      subscription_end: e.target.value
                    })}
                    min={subscriptionData.subscription_start}
                  />
                </div>
              </div>

              {/* Quick Duration Buttons */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quick Set Duration
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: '1 Month', months: 1 },
                    { label: '3 Months', months: 3 },
                    { label: '6 Months', months: 6 },
                    { label: '1 Year', months: 12 },
                  ].map((duration) => (
                    <button
                      key={duration.months}
                      type="button"
                      onClick={() => {
                        const start = new Date()
                        const end = new Date()
                        end.setMonth(end.getMonth() + duration.months)
                        setSubscriptionData({
                          ...subscriptionData,
                          subscription_start: start.toISOString().split('T')[0],
                          subscription_end: end.toISOString().split('T')[0]
                        })
                      }}
                      className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      {duration.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Max Users */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Users
                </label>
                <input
                  type="number"
                  className="input"
                  value={subscriptionData.max_users}
                  onChange={(e) => setSubscriptionData({
                    ...subscriptionData,
                    max_users: parseInt(e.target.value) || 10
                  })}
                  min={1}
                  max={9999}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Current users: {selectedCompany.user_count || 0}
                </p>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  className="input"
                  rows={2}
                  value={subscriptionData.subscription_notes}
                  onChange={(e) => setSubscriptionData({
                    ...subscriptionData,
                    subscription_notes: e.target.value
                  })}
                  placeholder="Internal notes about this subscription..."
                />
              </div>
            </form>

            {/* Actions */}
            <div className="flex justify-end gap-3 p-4 border-t flex-shrink-0">
              <button
                type="button"
                onClick={() => setIsSubscriptionModalOpen(false)}
                className="btn-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleSubscriptionSubmit}
                className="btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">Saving...</span>
                  </>
                ) : (
                  'Update Subscription'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Permissions Modal */}
      {permissionsCompany && (
        <RolePermissionsModal
          isOpen={isPermissionsModalOpen}
          onClose={() => {
            setIsPermissionsModalOpen(false)
            setPermissionsCompany(null)
          }}
          companyId={permissionsCompany.id}
          companyName={permissionsCompany.name}
        />
      )}

      {/* Modules Modal */}
      {isModulesModalOpen && modulesCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-xl font-bold">Module Toggles</h2>
                <p className="text-sm text-gray-500">{modulesCompany.name}</p>
              </div>
              <button
                onClick={() => setIsModulesModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-600">
                Enable or disable modules for this company. Disabled modules will be hidden from users and their API endpoints will return 403.
              </p>

              {[
                { key: 'kpi', label: 'KPI & Scoring', description: 'KPI indicators, submissions, scores, reports, and reminders' },
                { key: 'attendance', label: 'Attendance', description: 'Clock in/out, attendance tracking, and logs' },
                { key: 'leave', label: 'Leave Management', description: 'Leave requests, approvals, and balances' },
                { key: 'timelog', label: 'Time Tracker', description: 'Work time logging and timesheets' },
                { key: 'project_monitoring', label: 'Project Monitoring', description: 'Projects, tasks, Kanban board, Gantt chart, and comments' },
                { key: 'learning', label: 'Learning (Eagle Vision)', description: 'Enable Knowledge Base articles and questionnaires for this company' },
              ].map((mod) => (
                <div key={mod.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900">{mod.label}</div>
                    <div className="text-xs text-gray-500">{mod.description}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setModulesData({ ...modulesData, [mod.key]: !modulesData[mod.key] })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      modulesData[mod.key] ? 'bg-teal-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        modulesData[mod.key] ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 p-4 border-t">
              <button
                onClick={() => setIsModulesModalOpen(false)}
                className="btn-secondary"
                disabled={isModulesSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleModulesSave}
                className="btn-primary"
                disabled={isModulesSaving}
              >
                {isModulesSaving ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">Saving...</span>
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Delete Company</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-4">
                This will permanently delete <strong>{deleteConfirm.name}</strong> and all associated data including users, KPI data, submissions, scores, and settings.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type <strong>{deleteConfirm.code}</strong> to confirm
                </label>
                <input
                  type="text"
                  className="input"
                  value={deleteTyped}
                  onChange={(e) => setDeleteTyped(e.target.value)}
                  placeholder={deleteConfirm.code}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-lg">
              <button
                onClick={() => { setDeleteConfirm(null); setDeleteTyped('') }}
                className="btn-secondary"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCompany}
                disabled={deleteTyped !== deleteConfirm.code || isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Delete Company
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
